import { useCallback, useSyncExternalStore } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const STORAGE_KEY = 'meetup_travel_favorites';

interface FavoritesState {
  ids: Set<string>;
  loading: boolean;
  user: User | null;
}

let state: FavoritesState = { ids: new Set(), loading: true, user: null };
const listeners = new Set<() => void>();

function emit(next: FavoritesState) {
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): FavoritesState {
  return state;
}

function readLocal(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeLocal(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // ignore storage quota errors
  }
}

async function fetchRemoteIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase.from('vietnamtravel_favorites').select('tour_id').eq('user_id', userId);
  if (error) throw error;
  return new Set((data ?? []).map((row) => row.tour_id));
}

async function pushLocalToRemote(userId: string, localIds: Set<string>, remoteIds: Set<string>) {
  const toPush = Array.from(localIds).filter((id) => !remoteIds.has(id));
  if (toPush.length > 0) {
    await supabase
      .from('vietnamtravel_favorites')
      .insert(toPush.map((tourId) => ({ user_id: userId, tour_id: tourId })));
  }
}

async function syncToAccount(userId: string) {
  try {
    const remote = await fetchRemoteIds(userId);
    const local = readLocal();
    await pushLocalToRemote(userId, local, remote);
    const merged = new Set([...remote, ...local]);
    writeLocal(new Set());
    emit({ ids: merged, loading: false, user: state.user });
  } catch {
    emit({ ids: readLocal(), loading: false, user: state.user });
  }
}

let initialized = false;

function init() {
  if (initialized) return;
  initialized = true;

  supabase.auth
    .getSession()
    .then(({ data }) => {
      const user = data.session?.user ?? null;
      if (user) {
        state = { ...state, user };
        syncToAccount(user.id);
      } else {
        emit({ ids: readLocal(), loading: false, user: null });
      }
    })
    .catch(() => {
      emit({ ids: readLocal(), loading: false, user: null });
    });

  supabase.auth.onAuthStateChange((event, session) => {
    const user = session?.user ?? null;
    if (event === 'SIGNED_IN' && user) {
      state = { ...state, user, loading: true };
      emit(state);
      syncToAccount(user.id);
    } else if (event === 'SIGNED_OUT') {
      emit({ ids: readLocal(), loading: false, user: null });
    }
  });
}

init();

export async function toggleFavorite(tourId: string) {
  const { user, ids } = state;
  const has = ids.has(tourId);

  if (user) {
    const next = new Set(ids);
    if (has) next.delete(tourId);
    else next.add(tourId);
    emit({ ...state, ids: next });

    try {
      if (has) {
        await supabase.from('vietnamtravel_favorites').delete().eq('user_id', user.id).eq('tour_id', tourId);
      } else {
        await supabase.from('vietnamtravel_favorites').insert({ user_id: user.id, tour_id: tourId });
      }
    } catch {
      emit({ ...state, ids });
    }
  } else {
    const next = new Set(ids);
    if (has) next.delete(tourId);
    else next.add(tourId);
    writeLocal(next);
    emit({ ...state, ids: next });
  }
}

export function useFavorites() {
  const { ids, loading, user } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isFavorite = useCallback((id: string) => ids.has(id), [ids]);

  return {
    favoriteIds: ids,
    isFavorite,
    toggleFavorite,
    loading,
    isAuthenticated: !!user,
  };
}